import { useState } from "react";
import { FileText, ImageIcon } from "lucide-react";
import { Icon } from "@cntrl/ui";
import { useAttachmentUrl, type AttachmentDto } from "../../app/hooks/useInbox.js";
import { formatFileSize } from "../../app/hooks/inboxFormat.js";
import { ImageLightbox } from "./ImageLightbox.js";
import styles from "./Inbox.module.scss";

export interface MessageAttachmentProps {
  threadId: string;
  attachment: AttachmentDto;
}

/**
 * The ONE file a message carries, in the bubble (ART-159, C-Attach).
 *
 * **An IMAGE is shown and a FILE is a chip**, and the kind comes from the server rather than from the MIME
 * type: the write is what verified that an image is really one, so the read renders the verdict instead of
 * reaching it again. An unknown kind renders as a FILE — the conservative side, since the failure of being
 * wrong is a chip where a picture could have been, against an `<img>` pointed at a PDF.
 *
 * **The bytes come through an authenticated, participant-gated route**, so this cannot be a plain
 * `<img src={url}>` against the API — `useAttachmentUrl` fetches with the bearer and hands back an object
 * URL. Until it resolves (and if it never does — an expired chat, a tombstoned message) the image renders
 * its own chip rather than a broken-image glyph, which is why the `file` branch is the fallback for both.
 *
 * **A file's download is an `<a download>` with the object URL**, and that is a real limitation worth knowing:
 * it works here because the bytes are already in the page, where a link straight at the API would arrive
 * without the Authorization header and 401. An IMAGE opens the lightbox instead (ART-267), whose Save goes
 * through `imageActions.ts` — natively in the iOS app, where `<a download>` does nothing.
 */
export function MessageAttachment({ threadId, attachment }: MessageAttachmentProps) {
  const url = useAttachmentUrl(threadId, attachment.attachmentId);
  const name = attachment.fileName ?? "File";
  const size = formatFileSize(attachment.sizeBytes);
  const [viewing, setViewing] = useState(false);

  if (attachment.kind === "image" && url) {
    // A TAP OPENS IT FULL SCREEN (ART-267), where Save and Share live — rather than downloading the file
    // straight off the bubble, which inside the iOS app did nothing at all.
    return (
      <>
        <button
          type="button"
          className={styles.attachImageLink}
          aria-label={`Open photo ${name}`}
          onClick={() => setViewing(true)}
        >
          {/* The alt text is the file's own name: a photo in a chat has no other description, and "image"
              tells a screen-reader user strictly less than the sender's filename does. */}
          <img className={styles.attachImage} src={url} alt={name} />
        </button>
        {viewing && <ImageLightbox url={url} fileName={name} onClose={() => setViewing(false)} />}
      </>
    );
  }

  const chip = (
    <>
      <span className={styles.attachIcon} aria-hidden="true">
        <Icon icon={attachment.kind === "image" ? ImageIcon : FileText} size={16} />
      </span>
      <span className={styles.attachMeta}>
        <span className={styles.attachName}>{name}</span>
        {size && <span className={styles.attachSize}>{size}</span>}
      </span>
    </>
  );

  // No URL yet (or ever): a chip that names the file, not a broken image and not a spinner that may never
  // resolve. It becomes a link the moment the bytes land.
  return url ? (
    <a className={styles.attachChip} href={url} download={name}>
      {chip}
    </a>
  ) : (
    <span className={styles.attachChip} data-pending="true">
      {chip}
    </span>
  );
}
